import { useNavigate } from "@tanstack/react-router";
import { ClipboardList, Trash2 } from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useDeleteTemplate, useTemplates } from "@/hooks/use-templates";
import { formatDate } from "@/lib/format";
import { TEMPLATE_STATUS_BADGE_VARIANTS, TEMPLATE_STATUS_LABELS } from "@/lib/template-status";

export function TemplatesList() {
  const navigate = useNavigate();
  const { data: templates, isLoading, isError, refetch } = useTemplates();
  const deleteTemplate = useDeleteTemplate();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  if (isError) {
    return <ErrorState message="Failed to load templates" onRetry={() => refetch()} />;
  }

  return (
    <>
      <DataTable
        id="templates"
        headers={[
          { key: "name", label: "Name", defaultWidth: 220 },
          { key: "projectType", label: "Project Type", defaultWidth: 180 },
          { key: "status", label: "Status", defaultWidth: 110 },
          {
            key: "createdBy",
            label: "Created By",
            className: "hidden md:table-cell",
            defaultWidth: 160,
          },
          {
            key: "created",
            label: "Created",
            className: "hidden md:table-cell",
            defaultWidth: 120,
          },
          { key: "actions", label: "Actions", defaultWidth: 80 },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={ClipboardList}
            title="No templates yet"
            description="Create your first audit template to get started."
          />
        }
      >
        {(templates ?? []).map((template) => (
          <tr
            key={template.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
            onClick={() =>
              navigate({
                to: "/dashboard/templates/$templateId",
                params: { templateId: template.id },
              })
            }
          >
            <td className="px-4 py-3 font-medium">{template.name}</td>
            <td className="px-4 py-3 text-muted-foreground">{template.projectType}</td>
            <td className="px-4 py-3">
              <Badge variant={TEMPLATE_STATUS_BADGE_VARIANTS[template.status] ?? "secondary"}>
                {TEMPLATE_STATUS_LABELS[template.status] ?? template.status}
              </Badge>
            </td>
            <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
              {template.createdByName ?? "—"}
            </td>
            <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
              {formatDate(template.createdAt)}
            </td>
            <td className="px-4 py-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteId(template.id);
                }}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </td>
          </tr>
        ))}
      </DataTable>

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
        title="Delete template"
        description="This will permanently delete the template and all its sections and items. This action cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        loading={deleteTemplate.isPending}
        onConfirm={async () => {
          if (deleteId) {
            await deleteTemplate.mutateAsync(deleteId);
            setDeleteId(null);
          }
        }}
      />
    </>
  );
}
